import { getHSLAColor } from "@/helpers/style.helper.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { ApiPerformanceReviewReviewerStatsBase } from "@/types/performance-review.types.ts";
import { Box, Stack } from "@skailer-com/uikit";
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  type ChartData,
  ChartDataset,
  type ChartOptions,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Title as TitleChart,
  Tooltip,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useMemo, useRef } from "react";
import { Chart } from "react-chartjs-2";
import { useTranslation } from "react-i18next";

export const PerformanceReviewSummaryChart = ({
  reviewerStats,
}: {
  reviewerStats: ApiPerformanceReviewReviewerStatsBase;
}) => {
  const { t } = useTranslation();
  const chartRef = useRef(null);
  const { formatPeriodDates } = useFormatter();

  ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    TitleChart,
    Tooltip,
    Legend,
    LineController,
    BarController,
    ChartDataLabels
  );

  const chartVM = useMemo(() => {
    const completed: ChartDataset = {
      type: "line",
      data: [],
      borderColor: "#000",
      borderWidth: 2,
      pointBorderWidth: 3,
      backgroundColor: "white",
      borderJoinStyle: "bevel",
      pointRadius: 8,
      pointHoverRadius: 8,
      pointHoverBorderWidth: 3,
      pointHitRadius: 20,
      pointStyle: "circle",
      tension: 0.5,
      fill: false,
      yAxisID: "y",
    };
    const labels: string[] = [];

    for (const row of reviewerStats.completed_by_periods) {
      const title =
        formatPeriodDates({
          dateStart: row.date_start,
          dateEnd: row.date_end,
        })?.title || "";

      labels.push(title);
      completed.data.push(row.total_count);
    }

    return { labels, datasets: [completed] } as ChartData;
  }, [reviewerStats]);

  const totalCompletedMax = Math.max(
    ...reviewerStats.completed_by_periods.map((row) => row.total_count)
  );
  const totalCompletedMaxWithBounds = Math.ceil(totalCompletedMax * 1.1);

  const options: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      datalabels: {
        anchor: "end",
        align: "top",
        offset: 5,
        color: getHSLAColor("--color-brand-dark", 0.6),
        display: false,
        clamp: false,
        clip: false,
      },
      legend: {
        display: false,
      },
      title: {
        display: false,
      },

      tooltip: {
        displayColors: false,
        callbacks: {
          label: function (context) {
            const period_count = context.parsed.y ?? 0;

            const period_rate =
              reviewerStats.total_count > 0
                ? ((period_count / reviewerStats.total_count) * 100).toFixed() + "%"
                : "";

            return [
              t("surveys:participant_completed_label", {
                count: period_count,
              }),
              period_rate,
            ];
          },
        },
      },
    },
    hover: {
      intersect: false,
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        type: "linear",
        display: true,
        position: "right",
        max: totalCompletedMaxWithBounds,
        min: 0,
        border: {
          display: false,
        },

        grid: {
          color: function (context) {
            if (context.tick.value >= 0) {
              return getHSLAColor("--color-brand-dark", 0.1);
            } else if (context.tick.value < 0) {
              return "transparent";
            }
          },
        },
        ticks: {
          display: false,
        },
      },
    },
  };

  return (
    <Stack gap={"lg"}>
      <Box className={"h-[240px]"}>
        <Chart
          ref={chartRef}
          type={"line"}
          plugins={[ChartDataLabels]}
          options={options}
          data={chartVM}
        />
      </Box>
    </Stack>
  );
};
